import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import * as api from '../api/client';

const TABS = [
  { id: 'general', label: 'Genel' },
  { id: 'landing', label: 'Açılış' },
  { id: 'main', label: 'İçerik' },
  { id: 'gallery', label: 'Galeri' },
  { id: 'event', label: 'Etkinlik' },
  { id: 'music', label: 'Müzik' },
];

function defaultForm() {
  return {
    slug: '',
    published: false,
    couple: { bride: '', groom: '', initials: '' },
    landing: { title: '', hint: 'Açmak için mühüre dokunun', background: '' },
    envelope: { seal: '' },
    main: {
      tagline: '',
      message: '',
      headline: '',
      background: '',
      story: { title: 'Hikayemiz', quote: '', milestones: [] },
    },
    gallery: { left: [], right: [], mobile: [] },
    event: { dateISO: '', date: '', time: '', venue: '', address: '', mapUrl: '' },
    music: { src: '', title: '' },
  };
}

function milestonesToText(milestones) {
  return (milestones ?? []).map((m) => `${m.year}|${m.label}`).join('\n');
}

function textToMilestones(text) {
  return text
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const [year, ...rest] = line.split('|');
      return { year: year?.trim() ?? '', label: rest.join('|').trim() };
    });
}

function galleryToText(items) {
  return (items ?? []).map((g) => `${g.src}|${g.alt ?? ''}`).join('\n');
}

function textToGallery(text) {
  return text
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const [src, ...rest] = line.split('|');
      return { src: src?.trim() ?? '', alt: rest.join('|').trim() };
    });
}

export default function DigitalInvitationFormPage() {
  const { id: routeId } = useParams();
  const { pathname } = useLocation();
  const isNew = pathname.endsWith('/digital-invitations/new');
  const id = isNew ? null : routeId;
  const navigate = useNavigate();

  const [tab, setTab] = useState('general');
  const [form, setForm] = useState(defaultForm());
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (isNew || !id) return;
    (async () => {
      try {
        const data = await api.getDigitalInvitation(id);
        setForm({
          slug: data.slug ?? '',
          published: !!data.published,
          couple: { ...defaultForm().couple, ...data.couple },
          landing: { ...defaultForm().landing, ...data.landing },
          envelope: { ...defaultForm().envelope, ...data.envelope },
          main: {
            ...defaultForm().main,
            ...data.main,
            story: { ...defaultForm().main.story, ...data.main?.story },
          },
          gallery: {
            left: data.gallery?.left ?? [],
            right: data.gallery?.right ?? [],
            mobile: data.gallery?.mobile ?? [],
          },
          event: { ...defaultForm().event, ...data.event },
          music: { ...defaultForm().music, ...data.music },
        });
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [id, isNew]);

  const update = (path, value) => {
    setForm((prev) => {
      const next = { ...prev };
      const keys = path.split('.');
      let obj = next;
      for (let i = 0; i < keys.length - 1; i++) {
        obj[keys[i]] = { ...obj[keys[i]] };
        obj = obj[keys[i]];
      }
      obj[keys[keys.length - 1]] = value;
      return next;
    });
  };

  const handleUpload = async (e, fieldPath) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const media = await api.uploadMedia(file);
      update(fieldPath, media.url);
    } catch (err) {
      setError(err.message);
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const slug = form.slug.trim().toLowerCase().replace(/[^a-z0-9-]/g, '');
      if (!slug) throw new Error('Geçerli bir slug girin (örn. elifhakan)');

      const payload = {
        ...form,
        slug,
        landing: {
          ...form.landing,
          title: form.landing.title || `${form.couple.bride} & ${form.couple.groom}`,
        },
      };

      if (isNew) {
        const created = await api.createDigitalInvitation(payload);
        navigate(`/admin/digital-invitations/${created.id}/edit`, { replace: true });
        setSuccess('Dijital davetiye oluşturuldu');
      } else {
        await api.updateDigitalInvitation(id, payload);
        setSuccess('Kaydedildi');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="admin-loading">
        <p>Yükleniyor…</p>
      </div>
    );
  }

  const qrUrl = form.slug ? api.getDigitalInvitationQrUrl(form.slug) : '';

  return (
    <>
      <header className="admin-header">
        <h2>{isNew ? 'Yeni dijital davetiye' : 'Dijital davetiye düzenle'}</h2>
        <p>Kod yazmadan tüm içeriği buradan yönetin</p>
      </header>

      <div className="admin-content">
        <div className="admin-page-actions">
          <Link to="/admin/digital-invitations" className="admin-btn admin-btn--secondary">
            ← Listeye dön
          </Link>
          {form.published && form.slug && (
            <a href={`/${form.slug}`} target="_blank" rel="noreferrer" className="admin-btn admin-btn--secondary">
              Canlı önizle ↗
            </a>
          )}
        </div>

        {error && <div className="admin-alert admin-alert--error">{error}</div>}
        {success && <div className="admin-alert admin-alert--success">{success}</div>}

        <div className="admin-tabs">
          {TABS.map((t) => (
            <button key={t.id} type="button" className={tab === t.id ? 'active' : undefined} onClick={() => setTab(t.id)}>
              {t.label}
            </button>
          ))}
        </div>

        <form className="admin-form admin-form--wide" onSubmit={handleSave}>
          {tab === 'general' && (
            <>
              <div className="admin-grid-2">
                <div className="admin-field">
                  <label htmlFor="di-slug">Slug (URL yolu)</label>
                  <input id="di-slug" value={form.slug} onChange={(e) => update('slug', e.target.value)} placeholder="elifhakan" required />
                  <span className="admin-field__hint">Adres: /{form.slug || 'slug'}</span>
                </div>
                <div className="admin-field">
                  <label>
                    <input type="checkbox" checked={form.published} onChange={(e) => update('published', e.target.checked)} style={{ marginRight: '0.5rem' }} />
                    Yayında
                  </label>
                </div>
              </div>
              <div className="admin-grid-2">
                <div className="admin-field">
                  <label htmlFor="di-bride">Gelin adı</label>
                  <input id="di-bride" value={form.couple.bride} onChange={(e) => update('couple', { ...form.couple, bride: e.target.value })} />
                </div>
                <div className="admin-field">
                  <label htmlFor="di-groom">Damat adı</label>
                  <input id="di-groom" value={form.couple.groom} onChange={(e) => update('couple', { ...form.couple, groom: e.target.value })} />
                </div>
              </div>
              <div className="admin-field">
                <label htmlFor="di-initials">Baş harfler</label>
                <input id="di-initials" value={form.couple.initials} onChange={(e) => update('couple', { ...form.couple, initials: e.target.value })} placeholder="E&H" />
              </div>
              <div className="admin-field">
                <label htmlFor="di-seal">Mühür görseli</label>
                <input id="di-seal" value={form.envelope.seal} onChange={(e) => update('envelope', { seal: e.target.value })} />
                <input type="file" accept="image/*" onChange={(e) => handleUpload(e, 'envelope.seal')} style={{ marginTop: '0.5rem' }} />
              </div>
              {qrUrl && (
                <div className="admin-qr-preview">
                  <strong>QR kod linki:</strong><br />
                  {qrUrl}
                </div>
              )}
            </>
          )}

          {tab === 'landing' && (
            <>
              <div className="admin-field">
                <label htmlFor="di-land-title">Açılış başlığı</label>
                <input id="di-land-title" value={form.landing.title} onChange={(e) => update('landing', { ...form.landing, title: e.target.value })} />
              </div>
              <div className="admin-field">
                <label htmlFor="di-land-hint">Mühür ipucu metni</label>
                <input id="di-land-hint" value={form.landing.hint} onChange={(e) => update('landing', { ...form.landing, hint: e.target.value })} />
              </div>
              <div className="admin-field">
                <label htmlFor="di-land-bg">Arka plan görseli</label>
                <input id="di-land-bg" value={form.landing.background} onChange={(e) => update('landing', { ...form.landing, background: e.target.value })} />
                <input type="file" accept="image/*" onChange={(e) => handleUpload(e, 'landing.background')} style={{ marginTop: '0.5rem' }} />
              </div>
            </>
          )}

          {tab === 'main' && (
            <>
              <div className="admin-field">
                <label htmlFor="di-tagline">Slogan</label>
                <input id="di-tagline" value={form.main.tagline} onChange={(e) => update('main', { ...form.main, tagline: e.target.value })} />
              </div>
              <div className="admin-field">
                <label htmlFor="di-headline">Ana başlık</label>
                <input id="di-headline" value={form.main.headline} onChange={(e) => update('main', { ...form.main, headline: e.target.value })} />
              </div>
              <div className="admin-field">
                <label htmlFor="di-message">Davet mesajı</label>
                <textarea id="di-message" value={form.main.message} onChange={(e) => update('main', { ...form.main, message: e.target.value })} />
              </div>
              <div className="admin-field">
                <label htmlFor="di-main-bg">İçerik arka planı</label>
                <input id="di-main-bg" value={form.main.background} onChange={(e) => update('main', { ...form.main, background: e.target.value })} />
                <input type="file" accept="image/*" onChange={(e) => handleUpload(e, 'main.background')} style={{ marginTop: '0.5rem' }} />
              </div>
              <div className="admin-field">
                <label htmlFor="di-story-title">Hikaye başlığı</label>
                <input id="di-story-title" value={form.main.story.title} onChange={(e) => update('main', { ...form.main, story: { ...form.main.story, title: e.target.value } })} />
              </div>
              <div className="admin-field">
                <label htmlFor="di-story-quote">Hikaye alıntısı</label>
                <textarea id="di-story-quote" value={form.main.story.quote} onChange={(e) => update('main', { ...form.main, story: { ...form.main.story, quote: e.target.value } })} />
              </div>
              <div className="admin-field">
                <label htmlFor="di-milestones">Kilometre taşları (yıl|etiket, satır başına bir)</label>
                <textarea
                  id="di-milestones"
                  value={milestonesToText(form.main.story.milestones)}
                  onChange={(e) => update('main', { ...form.main, story: { ...form.main.story, milestones: textToMilestones(e.target.value) } })}
                  placeholder={'2020|İlk Buluşma\n2023|Nişan\n2026|Düğün'}
                />
              </div>
            </>
          )}

          {tab === 'gallery' && (
            <>
              <div className="admin-field">
                <label htmlFor="di-gal-left">Sol sütun (url|alt metin)</label>
                <textarea id="di-gal-left" value={galleryToText(form.gallery.left)} onChange={(e) => update('gallery', { ...form.gallery, left: textToGallery(e.target.value) })} />
              </div>
              <div className="admin-field">
                <label htmlFor="di-gal-right">Sağ sütun (url|alt metin)</label>
                <textarea id="di-gal-right" value={galleryToText(form.gallery.right)} onChange={(e) => update('gallery', { ...form.gallery, right: textToGallery(e.target.value) })} />
              </div>
              <div className="admin-field">
                <label htmlFor="di-gal-mobile">Mobil galeri (url|alt metin)</label>
                <textarea id="di-gal-mobile" value={galleryToText(form.gallery.mobile)} onChange={(e) => update('gallery', { ...form.gallery, mobile: textToGallery(e.target.value) })} />
              </div>
              <div className="admin-field">
                <label>Görsel yükle (URL otomatik eklenir)</label>
                <input type="file" accept="image/*" onChange={async (e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  setUploading(true);
                  try {
                    const media = await api.uploadMedia(file);
                    update('gallery', {
                      ...form.gallery,
                      mobile: [...form.gallery.mobile, { src: media.url, alt: file.name }],
                    });
                  } catch (err) {
                    setError(err.message);
                  } finally {
                    setUploading(false);
                  }
                }} />
                {uploading && <span className="admin-field__hint">Yükleniyor…</span>}
              </div>
            </>
          )}

          {tab === 'event' && (
            <>
              <div className="admin-grid-2">
                <div className="admin-field">
                  <label htmlFor="di-date">Tarih (görünen)</label>
                  <input id="di-date" value={form.event.date} onChange={(e) => update('event', { ...form.event, date: e.target.value })} placeholder="12 Eylül 2026 Cumartesi" />
                </div>
                <div className="admin-field">
                  <label htmlFor="di-time">Saat</label>
                  <input id="di-time" value={form.event.time} onChange={(e) => update('event', { ...form.event, time: e.target.value })} placeholder="19:00" />
                </div>
              </div>
              <div className="admin-field">
                <label htmlFor="di-date-iso">Tarih (ISO — geri sayım için)</label>
                <input id="di-date-iso" value={form.event.dateISO} onChange={(e) => update('event', { ...form.event, dateISO: e.target.value })} placeholder="2026-09-12T19:00:00" />
              </div>
              <div className="admin-field">
                <label htmlFor="di-venue">Mekan</label>
                <input id="di-venue" value={form.event.venue} onChange={(e) => update('event', { ...form.event, venue: e.target.value })} />
              </div>
              <div className="admin-field">
                <label htmlFor="di-address">Adres</label>
                <input id="di-address" value={form.event.address} onChange={(e) => update('event', { ...form.event, address: e.target.value })} />
              </div>
              <div className="admin-field">
                <label htmlFor="di-map">Harita linki</label>
                <input id="di-map" value={form.event.mapUrl} onChange={(e) => update('event', { ...form.event, mapUrl: e.target.value })} />
              </div>
            </>
          )}

          {tab === 'music' && (
            <>
              <div className="admin-field">
                <label htmlFor="di-music-title">Müzik başlığı</label>
                <input id="di-music-title" value={form.music.title} onChange={(e) => update('music', { ...form.music, title: e.target.value })} />
              </div>
              <div className="admin-field">
                <label htmlFor="di-music-src">Müzik dosyası URL</label>
                <input id="di-music-src" value={form.music.src} onChange={(e) => update('music', { ...form.music, src: e.target.value })} />
                <input type="file" accept="audio/*" onChange={(e) => handleUpload(e, 'music.src')} style={{ marginTop: '0.5rem' }} />
              </div>
            </>
          )}

          <div className="admin-form-actions">
            <button type="submit" className="admin-btn admin-btn--primary" disabled={saving}>
              {saving ? 'Kaydediliyor…' : 'Kaydet'}
            </button>
          </div>
        </form>
      </div>
    </>
  );
}
